import type { InstrumentId } from "@trader/shared/MarketData"
import { useSyncExternalStore } from "react"
import { call } from "../../api.ts"
import { replayEnd } from "../trading/accounts.ts"
import { accountsStore, accountStore, syncChartPreference } from "../trading/tradingData.ts"
import { type Quote, quoteOf } from "./watchlistModel.ts"

// The watchlist's quotes (docs/design/0014): each symbol's last two daily bars, as the charts
// see them, at the followed replay's time when the charts follow one (else now, the forming day
// built from minute bars). Polled while the panel is open, a few symbols at a time.

const POLL_MS = 15_000
const CONCURRENCY = 4
/** Enough calendar days to hold the previous session over a long weekend or holiday. */
const LOOKBACK_DAYS = 10

export type QuoteState = Quote | "loading" | "unavailable"

const quotes = new Map<InstrumentId, QuoteState>()
const listeners = new Set<() => void>()
const notify = () => {
  for (const listener of listeners) listener()
}

/** The charts' replay time (they follow the selected account's replay), or null for now. */
const asOfNow = (): number | null => replayEnd(accountStore.getState(), accountsStore.getState(), syncChartPreference.get() === "on")

const fetchQuote = async (id: InstrumentId, asOf: number | null): Promise<QuoteState> => {
  const now = asOf ?? Math.floor(Date.now() / 1000)
  try {
    const { candles } = await call((client) =>
      client.candles.get({
        query: {
          instrument: id,
          timeframe: "1D",
          from: now - LOOKBACK_DAYS * 86_400,
          to: now + 86_400,
          // Rounded to the minute, so repeated polls ask the same question.
          asOf: Math.floor(now / 60) * 60
        }
      })
    )
    return quoteOf(candles) ?? "unavailable"
  } catch {
    return "unavailable"
  }
}

let running = false
let again = false
let wanted: ReadonlyArray<InstrumentId> = []

/** Fetches the quotes of `ids`, CONCURRENCY at a time; asked again while it runs, it runs once more after. */
const refresh = async (ids: ReadonlyArray<InstrumentId>): Promise<void> => {
  wanted = ids
  if (running) {
    again = true
    return
  }
  running = true
  try {
    const asOf = asOfNow()
    const queue = [...ids]
    for (const id of ids) if (!quotes.has(id)) quotes.set(id, "loading")
    notify()
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
        for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
          const quote = await fetchQuote(id, asOf)
          // Keep the last good quote through a failed poll.
          if (quote !== "unavailable" || quotes.get(id) === "loading" || !quotes.has(id)) quotes.set(id, quote)
          notify()
        }
      })
    )
  } finally {
    running = false
  }
  if (again) {
    again = false
    await refresh(wanted)
  }
}

/**
 * Keeps `ids`' quotes fresh while the caller lives: now, every POLL_MS, and when the replay's
 * time moves. Returns the stop.
 */
export const watchQuotes = (ids: () => ReadonlyArray<InstrumentId>): (() => void) => {
  let lastAsOf = asOfNow()
  const run = () => void refresh(ids())
  const onReplay = () => {
    const asOf = asOfNow()
    if (asOf === lastAsOf) return
    lastAsOf = asOf
    run()
  }
  run()
  const timer = setInterval(run, POLL_MS)
  const stops = [accountStore.subscribe(onReplay), accountsStore.subscribe(onReplay), syncChartPreference.subscribe(onReplay)]
  return () => {
    clearInterval(timer)
    for (const stop of stops) stop()
  }
}

/** Fetches now (the panel's refresh, or a symbol just added). */
export const refreshQuotes = (ids: ReadonlyArray<InstrumentId>): Promise<void> => refresh(ids.length > 0 ? ids : wanted)

export const useQuote = (id: InstrumentId): QuoteState =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    () => quotes.get(id) ?? "loading"
  )
